import { ApiError, del, get, post, put } from './api';
import type {
  OrganizationSampleDataStatus,
  RemoveSampleDataResult,
  SampleDataTemplateContent,
  SampleDataTemplateRequest,
  SaveSampleDataTemplateResult,
  SampleDataTemplateResponse,
} from '../types/sampleData';
import {
  normalizeSampleDataTemplate,
  parseSampleDataFieldErrors,
} from '../utils/sampleDataTemplate';
import {
  normalizeRemoveSampleDataResult,
  normalizeSampleDataStatus,
} from '../utils/sampleDataStatus';

const TEMPLATE_URL = '/api/admin/sample-data/template';

const organizationSampleDataUrl = (orgId: string) =>
  `/api/admin/organizations/${encodeURIComponent(orgId)}/sample-data`;

/**
 * Loads the template. Throws `ApiError`; a 404 means this API build does not have the endpoint yet.
 */
export async function getSampleDataTemplate(
  signal?: AbortSignal
): Promise<SampleDataTemplateResponse> {
  const raw = await get<unknown>(TEMPLATE_URL, { signal });
  return normalizeSampleDataTemplate(raw);
}

/**
 * Saves the template. A validation failure resolves as `{ ok: false, errors }` so the form can put
 * each message next to its field; anything else still throws.
 */
export async function updateSampleDataTemplate(
  content: SampleDataTemplateContent,
  signal?: AbortSignal
): Promise<SaveSampleDataTemplateResult> {
  const body: SampleDataTemplateRequest = { content };
  try {
    const raw = await put<unknown>(TEMPLATE_URL, body, { signal });
    return { ok: true, template: normalizeSampleDataTemplate(raw) };
  } catch (err) {
    if (err instanceof ApiError && err.statusCode === 400) {
      const errors = parseSampleDataFieldErrors(err.response);
      if (errors) return { ok: false, errors };
    }
    throw err;
  }
}

/**
 * An organization's sample data status. Throws `ApiError`; a 404 means this API build does not have
 * the endpoint yet.
 */
export async function getOrganizationSampleData(
  orgId: string,
  signal?: AbortSignal
): Promise<OrganizationSampleDataStatus> {
  const raw = await get<unknown>(organizationSampleDataUrl(orgId), { signal });
  return normalizeSampleDataStatus(raw);
}

/**
 * Seeds the organization from the saved template. Throws `ApiError` with the server's message on
 * 409 (a set is already active) and 400 (no valid template saved).
 */
export async function addOrganizationSampleData(
  orgId: string,
  signal?: AbortSignal
): Promise<OrganizationSampleDataStatus> {
  const raw = await post<unknown>(organizationSampleDataUrl(orgId), undefined, { signal });
  return normalizeSampleDataStatus(raw);
}

/** Removes the organization's sample data, leaving anything coaches built on top of it. */
export async function removeOrganizationSampleData(
  orgId: string,
  signal?: AbortSignal
): Promise<RemoveSampleDataResult> {
  const raw = await del<unknown>(organizationSampleDataUrl(orgId), { signal });
  return normalizeRemoveSampleDataResult(raw);
}

/**
 * Tries adding the organization's sample calls again after they failed. Throws `ApiError` with the
 * server's message on 400 (no sample data, or calls already added) and 409 (no user to own them).
 */
export async function retrySampleCalls(
  orgId: string,
  signal?: AbortSignal
): Promise<OrganizationSampleDataStatus> {
  const raw = await post<unknown>(`${organizationSampleDataUrl(orgId)}/calls/retry`, undefined, {
    signal,
  });
  return normalizeSampleDataStatus(raw);
}
